import React, { ComponentType, useEffect } from 'react';
import { EMPTY, getSystemName } from '@app/consts/common_consts';
import { SCREEN_ID } from '@app/consts/env_consts';
import { checkEmptyValue } from '@app/utils/StringUtils';
import { useUnmountEffect } from 'primereact/hooks';
import { useTranslation } from 'react-i18next';
import { PrivateAndPublicRouteProps } from '../types';

type WithTabHandlerProps = Pick<AppRoute, 'screenId' | 'title'>;

const withTabHandler = <P extends PrivateAndPublicRouteProps>(
  WrappedComponent: ComponentType<P & WithTabHandlerProps>
) => {
  const HOC = (props: P) => {
    const { i18n } = useTranslation();

    const buildTitle = () => {
      const currentTitle = props.title;
      const systemName = getSystemName();
      const separator = ' | ';

      let baseTitle: string;
      if (currentTitle.includes(separator)) {
        baseTitle = currentTitle.split(separator)[0];
      } else if (currentTitle === systemName) {
        baseTitle = '';
      } else {
        baseTitle = currentTitle;
      }

      const translatedTitle = i18n.t(baseTitle);
      return translatedTitle ? checkEmptyValue(translatedTitle).concat(' | ', systemName) : systemName;
    };

    // Runs on mount, on route change (props.title changes), and on language change (i18n.language changes)
    useEffect(() => {
      document.title = buildTitle();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.title, i18n.language]);

    useEffect(() => {
      sessionStorage.setItem(SCREEN_ID, props.screenId);
    }, [props.screenId]);

    // When navigating away via hard reload (window.location.href), the browser may restore
    // this page from bfcache on back-button press. React effects do not re-run on bfcache
    // restore, leaving SCREEN_ID set to whatever the page navigated to had written.
    // The pageshow event fires with event.persisted=true on bfcache restore — re-set it here.
    useEffect(() => {
      const handlePageShow = (event: PageTransitionEvent) => {
        if (event.persisted) {
          sessionStorage.setItem(SCREEN_ID, props.screenId);
        }
      };

      window.addEventListener('pageshow', handlePageShow);

      return () => {
        window.removeEventListener('pageshow', handlePageShow);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useUnmountEffect(() => {
      document.title = getSystemName();
      sessionStorage.setItem(SCREEN_ID, EMPTY);
    });

    return <WrappedComponent {...props} />;
  };

  return HOC;
};

export default withTabHandler;
